import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { AgentRuleDTO, AgentRuleFormValues } from '../../types';
import { useAgentRuleAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

const ATTRIBUTE_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
type AttributeKey = `AppliedOnAttribute${(typeof ATTRIBUTE_KEYS)[number]}`;

const EMPTY_FORM: AgentRuleFormValues = {
  RuleName: '',
  RuleDescription: '',
  Enabled: false,
  AgentRuleTypeID: null,
  TriggerEventID: null,
  AppliedOnAttribute1: '',
  AppliedOnAttribute2: '',
  AppliedOnAttribute3: '',
  AppliedOnAttribute4: '',
  AppliedOnAttribute5: '',
  AppliedOnAttribute6: '',
  AppliedOnAttribute7: '',
  AppliedOnAttribute8: '',
  AppliedOnAttribute9: '',
};

/**
 * Restored Rules tab (Phase 2 of the Agent-editor sub-entity restoration — see
 * InstructionsPanel.tsx, this panel's direct structural template). Real entity (AIAgent_Rules)
 * fields confirmed by reading Rule.cs directly: RuleName/RuleDescription/Enabled/
 * AgentRuleTypeID/TriggerEventID/AppliedOnAttribute1-9 — not RulePriority/RuleExpression/
 * IsActive, which both the old deleted frontend and the design doc wrongly assumed. See
 * types.ts's AgentRuleDTO comment.
 */
export function RulesPanel({ agentId }: { agentId: number }) {
  const api = useAgentRuleAdminApiClient();
  const [rules, setRules] = useState<AgentRuleDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AgentRuleDTO | 'new' | null>(null);
  const [form, setForm] = useState<AgentRuleFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AgentRuleDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getRulesByAgent(agentId);
    setRules(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (rule: AgentRuleDTO | 'new') => {
    setEditing(rule);
    if (rule === 'new') {
      setForm(EMPTY_FORM);
      return;
    }
    const next: AgentRuleFormValues = {
      RuleName: rule.RuleName,
      RuleDescription: rule.RuleDescription ?? '',
      Enabled: rule.Enabled,
      AgentRuleTypeID: rule.AgentRuleTypeID ?? null,
      TriggerEventID: rule.TriggerEventID ?? null,
    };
    for (const n of ATTRIBUTE_KEYS) {
      const key = `AppliedOnAttribute${n}` as AttributeKey;
      next[key] = rule[key] ?? '';
    }
    setForm(next);
  };

  const handleSave = async () => {
    if (!form.RuleName.trim()) return;
    setSaving(true);
    const result = editing === 'new' ? await api.createRule(agentId, form) : await api.updateRule((editing as AgentRuleDTO).AgentRuleID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteRule(pendingDelete.AgentRuleID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setRules((prev) => prev.filter((r) => r.AgentRuleID !== pendingDelete.AgentRuleID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <div className="aaa-form-group aaa-form-group-full">
          <label>Rule Name *</label>
          <input type="text" value={form.RuleName} onChange={(e) => setForm((f) => ({ ...f, RuleName: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Rule Description</label>
          <input type="text" value={form.RuleDescription ?? ''} onChange={(e) => setForm((f) => ({ ...f, RuleDescription: e.target.value }))} />
        </div>
        <div className="aaa-form-group">
          <label>Agent Rule Type ID</label>
          <input
            type="number"
            value={form.AgentRuleTypeID ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, AgentRuleTypeID: e.target.value ? parseInt(e.target.value, 10) : null }))}
          />
        </div>
        <div className="aaa-form-group">
          <label>Trigger Event ID</label>
          <input
            type="number"
            value={form.TriggerEventID ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, TriggerEventID: e.target.value ? parseInt(e.target.value, 10) : null }))}
          />
        </div>
        <div className="aaa-form-group">
          <label className="aaa-checkbox">
            <input type="checkbox" checked={form.Enabled} onChange={(e) => setForm((f) => ({ ...f, Enabled: e.target.checked }))} />
            Enabled
          </label>
        </div>

        <fieldset className="aaa-form-group-full aaa-fieldset">
          <legend>Applied On Attributes</legend>
          <div className="aaa-form-grid">
            {ATTRIBUTE_KEYS.map((n) => {
              const key = `AppliedOnAttribute${n}` as AttributeKey;
              return (
                <div className="aaa-form-group" key={key}>
                  <label>Attribute {n}</label>
                  <input type="text" value={form[key] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
                </div>
              );
            })}
          </div>
        </fieldset>

        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Rule'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Rule
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && rules.length === 0 && <div className="aaa-entity-dropdown-empty">No rules yet.</div>}
      <div className="aaa-entity-grid">
        {rules.map((rule) => (
          <div key={rule.AgentRuleID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>{rule.RuleName}</strong>
              <span className={`aaa-badge ${rule.Enabled ? 'aaa-badge-active' : 'aaa-badge-inactive'}`}>
                {rule.Enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {rule.RuleDescription && <p>{rule.RuleDescription}</p>}
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(rule)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(rule)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Rule"
        message={`Delete rule "${pendingDelete?.RuleName}"?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
